import { type ButtonHTMLAttributes, forwardRef } from 'react';

import styles from './Button.module.css';

/**
 * 버튼 변형. Primary는 한 화면에 하나의 핵심 행동에만 사용한다.
 * - `primary`   : 빨간 크레용 배경 + 잉크 그림자 (예: "코드 입력", "다운로드")
 * - `secondary` : 종이색 배경 + 잉크 그림자 (예: "다시 보기")
 * - `ghost`     : 배경 없음 + 점선 보더 (예: "SNS 공유")
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** 버튼 시각 스타일. 기본값은 `primary`. */
  variant?: ButtonVariant;
  /** 너비 100% 모드 — 폼 제출 버튼 등에 사용. */
  fullWidth?: boolean;
};

/**
 * 디자인 시스템 베이스 버튼.
 *
 * - 호버 시 그림자가 살짝 멀어지고, 클릭 시 그림자가 사라져 "도장 찍히는" 인상을 만든다.
 * - `disabled` 상태에선 종이톤 + 회색 잉크로 비활성 처리된다.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', fullWidth = false, className, type = 'button', children, ...rest },
  ref,
) {
  const classes = [
    styles.btn,
    styles[variant],
    fullWidth ? styles.fullWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type={type} className={classes} {...rest}>
      {children}
    </button>
  );
});
